import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { useUser } from '../../contexts/UserContext';
import { useVerifyMagicLink } from '../../services/auth';
import PublicLayout from '../layouts/PublicLayout';

/**
 * MagicLinkHandler - Handles magic link token verification and auto sign-in.
 * 
 * When a user clicks a magic link (e.g., from an email), this component:
 * 1. Extracts the token from the URL query params
 * 2. Signs out any currently logged-in user (if different from token's user)
 * 3. Verifies the token with the backend
 * 4. Signs in the user associated with the token
 * 5. Redirects to the intended page (e.g., /processing/agreement)
 */
const MagicLinkHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signOut } = useUser();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  const token = searchParams.get('token');

  const verifyMagicLink = useVerifyMagicLink((data) => {
    // Successfully verified - sign in the user
    signIn(data, () => {
      // Navigate to the redirect path (from token) or default to the
      // onboarding timeline. (Legacy DocuSeal tokens carry their own
      // /processing/agreement redirectPath and keep working.)
      const redirectPath = data._redirectPath || '/processing';
      navigate(redirectPath, { replace: true });
    });
  }, {
    onError: (err) => {
      setError(err?.response?.data?.msg || err?.message || 'Failed to verify link. Please try again.');
      setIsProcessing(false);
    },
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid link. No token provided.');
      setIsProcessing(false);
      return;
    }

    const processToken = async () => {
      try {
        // Clear any existing session first to ensure clean sign-in
        // This handles case (2): if logged into another account, sign them out first
        if (user) {
          // Sign out synchronously before verifying the new token
          signOut();
        }

        // Small delay to ensure sign out completes
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Now verify the magic link token
        verifyMagicLink.mutate(token);
      } catch (err) {
        setError('An error occurred. Please try again.');
        setIsProcessing(false);
      }
    };

    processToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <PublicLayout>
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
        {isProcessing && !error && (
          <>
            <Spinner animation="border" role="status" className="mb-3" />
            <p className="text-muted">Signing you in...</p>
          </>
        )}
        {error && (
          <Alert variant="danger" className="text-center">
            <Alert.Heading>Unable to sign in</Alert.Heading>
            <p>{error}</p>
            <hr />
            <p className="mb-0">
              <a href="/sign-in">Click here to sign in manually</a>
            </p>
          </Alert>
        )}
      </div>
    </PublicLayout>
  );
};

export default MagicLinkHandler;

